import { useIsVoiceWorkspaceEnabled } from '@/features/flags';
import { sessionListCanFitSplitAtom } from '@/features/sessions/context/sessions-panel-atoms';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { useSyncExternalStore } from 'react';

import {
  voiceProjectsLhsHostCountAtom,
  voiceSessionRhsHostCountAtom,
} from '@heidi/agents';
import { useVoiceAssistantStore } from '../store/useVoiceAssistantStore';

/**
 * Where maximized Voice paints. Pure function of route + workspace flag +
 * whether the chosen host can actually paint Voice.
 *
 * - `session-rhs` — `/scribe/sessions/*` when split layout + an active session
 *   + SessionWorkVoiceSidebarSync mounted + Evidence aside visible (md+)
 * - `projects-lhs` — `/scribe/chat/projects/*` when ProjectsArtifactCanvasHost
 *   is mounted (Work gate / NotFound paths do not mount it)
 * - `overlay` — elsewhere, workspace flag off, or host slot unavailable
 */
export type VoicePlacement = 'session-rhs' | 'projects-lhs' | 'overlay';

/** Tailwind `md` — matches EvidenceSidebarPanel aside `hidden md:flex`. */
const EVIDENCE_RHS_VISIBLE_MQ = '(min-width: 768px)';

function subscribeEvidenceRhsVisible(onChange: () => void) {
  const mql = window.matchMedia(EVIDENCE_RHS_VISIBLE_MQ);
  mql.addEventListener('change', onChange);
  return () => mql.removeEventListener('change', onChange);
}

function getEvidenceRhsVisible() {
  return window.matchMedia(EVIDENCE_RHS_VISIBLE_MQ).matches;
}

/** Evidence RHS aside is CSS-visible (`md:flex`). SSR assumes yes. */
function useEvidenceRhsHostVisible(): boolean {
  return useSyncExternalStore(
    subscribeEvidenceRhsVisible,
    getEvidenceRhsVisible,
    () => true
  );
}

export function resolveVoicePlacement(
  pathname: string | undefined,
  workspaceEnabled: boolean,
  sessionCanFitSplit: boolean,
  hasActiveSession: boolean,
  projectsLhsHostMounted: boolean,
  /**
   * Evidence RHS host present (SessionWorkVoiceSidebarSync). Defaults true so
   * call sites that only care about route/split keep working.
   */
  sessionRhsHostMounted = true,
  /** Evidence RHS is `hidden md:flex` — mobile cannot paint session-rhs. */
  sessionRhsHostVisible = true
): VoicePlacement {
  if (!workspaceEnabled) return 'overlay';
  // Plural route only — `/scribe/session` is a redirect stub without the RHS host.
  if (pathname?.startsWith('/scribe/sessions')) {
    // Host body requires sessionId + Work/Ask Heidi Sync; without them overlay
    // keeps dock/window reachable. Mobile: RHS host is CSS-hidden.
    return sessionCanFitSplit &&
      hasActiveSession &&
      sessionRhsHostMounted &&
      sessionRhsHostVisible
      ? 'session-rhs'
      : 'overlay';
  }
  if (pathname?.startsWith('/scribe/chat/projects')) {
    return projectsLhsHostMounted ? 'projects-lhs' : 'overlay';
  }
  return 'overlay';
}

export function useVoicePlacement(): VoicePlacement {
  const workspaceEnabled = useIsVoiceWorkspaceEnabled();
  const { pathname } = useRouter();
  // Defaults true off the sessions page (atom resets on unmount).
  const sessionCanFitSplit = useAtomValue(sessionListCanFitSplitAtom);
  const hasActiveSession = Boolean(useCurrentSessionId());
  const projectsLhsHostMounted = useAtomValue(voiceProjectsLhsHostCountAtom) > 0;
  const sessionRhsHostMounted = useAtomValue(voiceSessionRhsHostCountAtom) > 0;
  const sessionRhsHostVisible = useEvidenceRhsHostVisible();
  return resolveVoicePlacement(
    pathname,
    workspaceEnabled,
    sessionCanFitSplit,
    hasActiveSession,
    projectsLhsHostMounted,
    sessionRhsHostMounted,
    sessionRhsHostVisible
  );
}

/** True when Voice should paint in this layout slot (open + not minimized). */
export function useVoiceAt(placement: Exclude<VoicePlacement, 'overlay'>): boolean {
  const current = useVoicePlacement();
  const workspace = useVoiceAssistantStore((s) => s.workspace);
  return current === placement && !!workspace && !workspace.minimized;
}
